import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";

import { SectionHeading } from "@/components/section-heading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  getMyApplication,
  listMyAppealTimeline,
  listMyNotices,
  resendMyLatestNotice,
  submitAppeal,
} from "@/lib/account.functions";

export const Route = createFileRoute("/_authenticated/account/status")({
  validateSearch: z.object({ section: z.string().optional() }),
  head: () => ({
    meta: [
      { title: "Account status — Quibblies" },
      {
        name: "description",
        content:
          "Track your Quibblies membership application, read the team's decision and submit an appeal.",
      },
      { property: "og:title", content: "Account status — Quibblies" },
      { property: "og:description", content: "Your application status and decision messages." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AccountStatusPage,
});

const STAGES = [
  { key: "signup", label: "Sign up" },
  { key: "pending_email", label: "Verify email" },
  { key: "pending_review", label: "Team review" },
  { key: "approved", label: "Approved" },
] as const;

/** Friendly wording for the review stages recorded against an appeal. */
const STAGE_LABELS: Record<string, string> = {
  appeal_submitted: "Appeal received",
  appeal_queued: "Waiting for an independent reviewer",
  appeal_in_review: "Being reviewed",
  decision_approved: "Approved",
  decision_rejected: "Decision upheld",
};

function AccountStatusPage() {
  const queryClient = useQueryClient();
  const loadApplication = useServerFn(getMyApplication);
  const loadNotices = useServerFn(listMyNotices);
  const loadTimeline = useServerFn(listMyAppealTimeline);
  const appeal = useServerFn(submitAppeal);
  const resend = useServerFn(resendMyLatestNotice);
  const { section } = Route.useSearch();
  const [message, setMessage] = React.useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["my-application"],
    queryFn: () => loadApplication(),
    retry: false,
  });
  const { data: notices } = useQuery({
    queryKey: ["my-notices"],
    queryFn: () => loadNotices(),
    retry: false,
  });
  const { data: timeline } = useQuery({
    queryKey: ["my-appeal-timeline"],
    queryFn: () => loadTimeline(),
    retry: false,
  });

  // Approval links carry ?section=… so the right part of the page is shown
  // every time — on mobile, after a refresh, or from an email.
  React.useEffect(() => {
    if (!section) return;
    const timer = window.setTimeout(() => {
      document.getElementById(section)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [section, isLoading]);

  const resendMutation = useMutation({
    mutationFn: () => resend({ data: undefined }),
    onSuccess: (result) => {
      if (result.resent) {
        toast.success("Message sent again", {
          description: "Check your inbox — a fresh copy is also in your Quibblies messages.",
        });
      } else if (result.reason === "too_soon") {
        toast.info("Just a moment", { description: "You can resend again in a few minutes." });
      } else {
        toast.info("Nothing to resend yet", {
          description: "There are no decision messages on your account.",
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["my-notices"] });
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
    onError: () => toast.error("We couldn't resend that message. Try again shortly."),
  });

  const appealMutation = useMutation({
    mutationFn: () => appeal({ data: { message } }),
    onSuccess: () => {
      toast.success("Appeal sent", {
        description: "A different team member will read it and reply here.",
      });
      setMessage("");
      void queryClient.invalidateQueries({ queryKey: ["my-application"] });
      void queryClient.invalidateQueries({ queryKey: ["my-appeal-timeline"] });
      void queryClient.invalidateQueries({ queryKey: ["my-notices"] });
    },
    onError: (e: Error) => toast.error("Could not send the appeal", { description: e.message }),
  });

  const status = data?.application_status ?? "pending_email";
  const activeIndex =
    status === "approved" ? 3 : status === "pending_review" || status === "rejected" ? 2 : 1;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Your account status"
        description="Every Quibblies account is checked by a person before it is opened up. Here is exactly where yours is."
      />

      {isLoading ? (
        <Skeleton className="mt-8 h-40 w-full rounded-2xl" />
      ) : (
        <>
          <ol className="mt-8 flex flex-wrap gap-2 text-xs uppercase tracking-wide">
            {STAGES.map((stage, index) => (
              <li
                key={stage.key}
                className={
                  index < activeIndex
                    ? "rounded-full bg-emerald-500/15 px-3 py-1.5 text-emerald-300"
                    : index === activeIndex
                      ? "rounded-full bg-primary/20 px-3 py-1.5 text-foreground"
                      : "rounded-full bg-muted/40 px-3 py-1.5 text-muted-foreground"
                }
              >
                {stage.label}
              </li>
            ))}
          </ol>

          <div className="mt-6 rounded-2xl border border-border/60 bg-surface/50 p-5">
            <Badge variant={status === "rejected" ? "destructive" : "outline"}>
              {String(status).replace("_", " ")}
            </Badge>
            <p className="mt-3 text-sm text-muted-foreground">
              {status === "pending_email"
                ? "Confirm your email address using the link we sent. Your application moves to the team as soon as you do."
                : status === "pending_review"
                  ? "Your application is with the team. Decisions usually take under 48 hours."
                  : status === "approved"
                    ? "You're approved — everything on Quibblies is open to you."
                    : "Your application was not approved."}
            </p>
            {data?.application_note ? (
              <p className="mt-3 rounded-xl bg-background/40 p-3 text-sm text-foreground">
                {data.application_note}
              </p>
            ) : null}
          </div>

          {status === "rejected" ? (
            <div className="mt-6 rounded-2xl border border-border/60 bg-surface/50 p-5">
              <h2 className="font-display text-lg text-foreground">Appeal this decision</h2>
              {data?.appeal_submitted_at ? (
                <p className="mt-2 text-sm text-muted-foreground">
                  Your appeal was received on{" "}
                  {new Date(data.appeal_submitted_at).toLocaleDateString()} and is being read by a
                  different team member.
                </p>
              ) : (
                <>
                  <p className="mt-2 text-sm text-muted-foreground">
                    You have 30 days to appeal. Tell us what you think was misunderstood.
                  </p>
                  <Textarea
                    className="mt-3"
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Explain your appeal…"
                  />
                  <Button
                    className="mt-3"
                    size="sm"
                    variant="hero"
                    disabled={appealMutation.isPending}
                    onClick={() => appealMutation.mutate()}
                  >
                    Send appeal
                  </Button>
                </>
              )}
            </div>
          ) : null}

          {timeline?.length ? (
            <section id="timeline" className="mt-10 scroll-mt-24">
              <h2 className="font-display text-xl text-foreground">Where your review is</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Updated as your appeal moves through the team. This matches the messages below.
              </p>
              <ol className="mt-4 space-y-4 border-l border-border/60 pl-5">
                {timeline.map((event, index) => (
                  <li key={event.id} className="relative">
                    <span
                      aria-hidden="true"
                      className={`absolute -left-[1.6rem] top-1.5 h-2.5 w-2.5 rounded-full ${
                        index === timeline.length - 1 ? "bg-primary" : "bg-border"
                      }`}
                    />
                    <p className="text-sm font-medium text-foreground">
                      {STAGE_LABELS[event.stage] ?? event.stage.replace(/_/g, " ")}
                    </p>
                    {event.detail ? (
                      <p className="mt-1 text-sm text-muted-foreground">{event.detail}</p>
                    ) : null}
                    <p className="mt-1 text-[0.7rem] uppercase tracking-wide text-muted-foreground">
                      {new Date(event.created_at).toLocaleString()}
                    </p>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}

          <div
            id="messages"
            className="mt-10 flex flex-wrap items-center justify-between gap-3 scroll-mt-24"
          >
            <h2 className="font-display text-xl text-foreground">Decision messages</h2>
            <div className="flex items-center gap-2">
              <Button asChild variant="ghost" size="sm">
                <Link to="/messages">Open messages inbox</Link>
              </Button>
              <Button
                variant="soft"
                size="sm"
                disabled={resendMutation.isPending}
                onClick={() => resendMutation.mutate()}
              >
                Resend my notification
              </Button>
            </div>
          </div>
          {!notices?.length ? (
            <p className="mt-3 text-sm text-muted-foreground">No messages yet.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {notices.map((notice) => (
                <li
                  key={notice.id}
                  className="rounded-xl border border-border/60 bg-surface/40 p-4"
                >
                  <p className="text-sm font-medium text-foreground">{notice.subject}</p>
                  <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
                    {notice.body}
                  </p>
                  <p className="mt-2 text-[0.7rem] uppercase tracking-wide text-muted-foreground">
                    {new Date(notice.created_at).toLocaleString()} · {notice.delivery_status}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
